/** Small app-wide dialogs opened imperatively: name prompt, confirm, colour & icon. */
import { useState } from 'react';
import { create } from 'zustand';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/overlay';
import { cn } from '@/lib/cn';
import { SPACE_COLORS } from '@karya/core';

type DialogSpec =
  | {
      kind: 'name';
      title: string;
      label: string;
      initial?: string;
      submit: string;
      onSubmit: (v: string) => void | Promise<void>;
    }
  | {
      kind: 'confirm';
      title: string;
      body: string;
      confirm: string;
      destructive?: boolean;
      onConfirm: () => void | Promise<void>;
    }
  | {
      kind: 'appearance';
      title: string;
      color: string | null;
      emoji?: string | null;
      showEmoji: boolean;
      onSubmit: (v: { color: string | null; emoji: string | null }) => void | Promise<void>;
    };

const useDialogs = create<{
  spec: DialogSpec | null;
  open: (s: DialogSpec) => void;
  close: () => void;
}>((set) => ({
  spec: null,
  open: (spec) => set({ spec }),
  close: () => set({ spec: null }),
}));

export const openDialog = (s: DialogSpec) => useDialogs.getState().open(s);

const COLORS = [...SPACE_COLORS, '#0f766e', '#e11d48', '#64748b'];
const EMOJI = [
  '🎯',
  '⚡',
  '🔁',
  '📦',
  '✅',
  '📋',
  '🏠',
  '💼',
  '💡',
  '📅',
  '💰',
  '🛒',
  '❤️',
  '📚',
  '🧘',
  '✈️',
  '🧾',
  '🔥',
];

export function DialogHost() {
  const { spec, close } = useDialogs();
  return (
    <Dialog open={!!spec} onOpenChange={(o) => !o && close()}>
      {spec && <DialogBody spec={spec} close={close} />}
    </Dialog>
  );
}

function DialogBody({ spec, close }: { spec: DialogSpec; close: () => void }) {
  const [value, setValue] = useState(spec.kind === 'name' ? (spec.initial ?? '') : '');
  const [color, setColor] = useState(spec.kind === 'appearance' ? spec.color : null);
  const [emoji, setEmoji] = useState(spec.kind === 'appearance' ? (spec.emoji ?? '') : '');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => void | Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      close();
    } finally {
      setBusy(false);
    }
  };
  if (spec.kind === 'name') {
    return (
      <DialogContent title={spec.title} width={420}>
        <form
          className="flex flex-col gap-3 px-4 pb-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) void run(() => spec.onSubmit(value.trim()));
          }}
        >
          <label className="flex flex-col gap-1 text-sm text-ink-2">
            {spec.label}
            <input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="h-9 rounded-sm border border-line bg-bg px-2 text-base text-ink outline-none focus:border-primary"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!value.trim() || busy}>
              {spec.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    );
  }
  if (spec.kind === 'confirm') {
    return (
      <DialogContent title={spec.title} width={420}>
        <div className="flex flex-col gap-4 px-4 pb-4">
          <p className="text-ink-2">{spec.body}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant={spec.destructive ? 'danger' : 'primary'}
              disabled={busy}
              onClick={() => void run(spec.onConfirm)}
            >
              {spec.confirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    );
  }
  return (
    <DialogContent title={spec.title} width={420}>
      <div className="flex flex-col gap-4 px-4 pb-4">
        <div className="flex flex-col gap-2">
          <span className="text-sm text-ink-2">Colour</span>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                aria-label={c}
                aria-pressed={color === c}
                onClick={() => setColor(c)}
                className={cn(
                  'size-7 rounded-sm ring-offset-2 ring-offset-raised',
                  color === c && 'ring-2 ring-primary',
                )}
                style={{ background: c }}
              />
            ))}
          </div>
        </div>
        {spec.showEmoji && (
          <div className="flex flex-col gap-2">
            <label htmlFor="emoji" className="text-sm text-ink-2">
              Icon (emoji)
            </label>
            <div className="flex flex-wrap gap-1">
              {EMOJI.map((e) => (
                <button
                  key={e}
                  onClick={() => setEmoji(e)}
                  aria-pressed={emoji === e}
                  className={cn(
                    'size-8 rounded-sm text-lg hover:bg-surface-2',
                    emoji === e && 'bg-primary-soft',
                  )}
                >
                  {e}
                </button>
              ))}
            </div>
            <input
              id="emoji"
              value={emoji}
              onChange={(e) => setEmoji(e.target.value)}
              placeholder="Or type/paste an emoji"
              className="h-9 rounded-sm border border-line bg-bg px-2 outline-none focus:border-primary"
            />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => void run(() => spec.onSubmit({ color, emoji: emoji.trim() || null }))}
          >
            Save
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}
